import { Link } from 'react-router-dom';
import type { Movie } from '../stores/movieStore';

interface MovieCardProps {
  movie: Movie;
  showSourceCount?: boolean;
}

const MovieCard = ({ movie, showSourceCount = true }: MovieCardProps) => {
  const getRatingColor = (rating: number) => {
    if (rating >= 8) return 'text-green-400';
    if (rating >= 6) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <Link to={`/movie/${movie.id}`} className="group block">
      <div className="relative overflow-hidden rounded-2xl bg-slate-800/50 card-hover">
        {/* 海报图片 */}
        <div className="aspect-[2/3] overflow-hidden">
          <img
            src={movie.poster}
            alt={movie.title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
            loading="lazy"
          />
          {/* 渐变遮罩 */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        {/* 评分标签 */}
        <div className="absolute top-3 right-3">
          <div className="flex items-center space-x-1 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-lg">
            <svg className={`w-4 h-4 ${getRatingColor(movie.rating)}`} fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className={`text-sm font-semibold ${getRatingColor(movie.rating)}`}>
              {movie.rating.toFixed(1)}
            </span>
          </div>
        </div>

        {/* 年份标签 */}
        <div className="absolute top-3 left-3">
          <span className="px-2 py-1 bg-blue-500/80 backdrop-blur-sm text-white text-xs font-medium rounded-lg">
            {movie.year}
          </span>
        </div>

        {/* 底部信息（悬浮显示） */}
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <h3 className="text-white font-bold text-lg mb-2 line-clamp-1">
            {movie.title}
          </h3>
          <div className="flex flex-wrap gap-2 mb-2">
            {movie.genre.slice(0, 3).map((g) => (
              <span
                key={g}
                className="px-2 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs rounded-full"
              >
                {g}
              </span>
            ))}
          </div>
          {showSourceCount && movie.sources && (
            <p className="text-gray-300 text-sm">
              {movie.sources.length} 个播放源
            </p>
          )}
        </div>
      </div>

      {/* 标题信息（始终显示） */}
      <div className="mt-3 px-1">
        <h3 className="text-white font-medium text-sm line-clamp-1 group-hover:text-blue-400 transition-colors">
          {movie.title}
        </h3>
        <p className="text-gray-400 text-xs mt-1">
          {movie.year} · {movie.genre[0] || '未知'}
        </p>
      </div>
    </Link>
  );
};

export default MovieCard;
